<script lang="ts">
  import { onMount } from 'svelte'
  import { api } from '$lib/api/client'
  import type { paths } from '$lib/api/generated'
  import Table from '../../components/Table.svelte'
  import SearchInput from '../../components/SearchInput.svelte'
  import Modal from '../../components/Modal.svelte'
  import FormField from '../../components/FormField.svelte'
  import { showApiError } from '$lib/util/errors'
  import { debounce } from '$lib/util/debounce'

  type Person =
    paths['/people']['get']['responses'][200]['content']['application/json']['items'][number]

  let items = $state<Person[]>([])
  let nextCursor = $state<number | undefined>(undefined)
  let search = $state('')
  let loading = $state(false)
  let showModal = $state(false)
  let editing = $state<Person | null>(null)

  let firstName = $state('')
  let lastName = $state('')
  let email = $state('')

  async function load(cursor?: number): Promise<void> {
    loading = true
    const { data, error } = await api.GET('/people', {
      params: {
        query: {
          limit: 50,
          ...(cursor !== undefined ? { cursor } : {}),
          ...(search ? { search } : {}),
        },
      },
    })
    if (error) {
      showApiError(error)
      loading = false
      return
    }
    items = cursor !== undefined ? [...items, ...(data?.items ?? [])] : (data?.items ?? [])
    nextCursor = data?.nextCursor
    loading = false
  }

  function openCreate(): void {
    editing = null
    firstName = ''
    lastName = ''
    email = ''
    showModal = true
  }

  function openEdit(person: Person): void {
    editing = person
    firstName = person.firstName
    lastName = person.lastName
    email = person.email
    showModal = true
  }

  async function handleSubmit(e: SubmitEvent): Promise<void> {
    e.preventDefault()
    if (editing) {
      const { data, error } = await api.PUT('/people/{id}', {
        params: { path: { id: editing.id } },
        body: { firstName, lastName, email },
      })
      if (error || !data) {
        showApiError(error)
        return
      }
      items = items.map((p) => (p.id === editing?.id ? data : p))
    } else {
      const { data, error } = await api.POST('/people', {
        body: { firstName, lastName, email },
      })
      if (error || !data) {
        showApiError(error)
        return
      }
      items = [data, ...items]
    }
    showModal = false
  }

  async function handleDelete(id: number): Promise<void> {
    if (!confirm('Delete this person?')) return
    const { error } = await api.DELETE('/people/{id}', { params: { path: { id } } })
    if (error) {
      showApiError(error)
      return
    }
    items = items.filter((p) => p.id !== id)
  }

  const searchDebounced = debounce(() => void load(), 300)
  $effect(() => {
    void search
    searchDebounced.call()
    return () => searchDebounced.cancel()
  })

  onMount(() => {
    void load()
  })
</script>

<div class="section-header">
  <h1>People</h1>
  <div class="section-actions">
    <SearchInput bind:value={search} placeholder="Search name or email" />
    <button type="button" class="primary" onclick={openCreate}>+ Add person</button>
  </div>
</div>

<Table
  {items}
  {loading}
  columns={[
    { key: 'firstName', label: 'First name' },
    { key: 'lastName', label: 'Last name' },
    { key: 'email', label: 'Email' },
  ]}
  onEdit={openEdit}
  onDelete={handleDelete}
/>

{#if nextCursor !== undefined}
  <button type="button" class="load-more" onclick={() => void load(nextCursor)} disabled={loading}>
    {loading ? 'Loading…' : 'Load more'}
  </button>
{/if}

{#if showModal}
  <Modal title={editing ? 'Edit person' : 'Add person'} onclose={() => (showModal = false)}>
    <form onsubmit={handleSubmit}>
      <FormField label="First name" name="firstName" bind:value={firstName} required />
      <FormField label="Last name" name="lastName" bind:value={lastName} required />
      <FormField label="Email" name="email" type="email" bind:value={email} required />
      <button type="submit" class="primary">Save</button>
    </form>
  </Modal>
{/if}

<style>
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }
  h1 {
    margin: 0;
    font-size: 1.75rem;
  }
  .section-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  button.primary {
    background: var(--brand);
    color: white;
    padding: 6px 14px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
  }
  button.primary:hover {
    background: var(--brand-hover);
  }
  button.load-more {
    padding: 8px 16px;
    background: var(--surface-2);
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    cursor: pointer;
  }
  button.load-more:hover:not(:disabled) {
    background: var(--surface-3);
  }
</style>
